import React, { useEffect, useRef, useState } from 'react';

/**
 * Analytics Layer — three cards rendered as pure HTML / SVG:
 *   - Issue Type Distribution donut (Story / Bug / Task / Security)
 *   - Quality Hotspots horizontal bars
 *   - Estimate vs Logged 2x2 stat grid
 *
 * Each card reveals once when it enters the viewport (subtle one-shot animation).
 * Reduced motion users see the final state immediately.
 */

const HOTSPOTS: readonly { module: string; count: number }[] = [
  { module: 'Notifications', count: 23 },
  { module: 'Checkout', count: 17 },
  { module: 'Integrations', count: 14 },
  { module: 'Reporting', count: 11 },
  { module: 'API', count: 9 },
] as const;

const RING_RADIUS = 44;
const RING_CIRCUM = 2 * Math.PI * RING_RADIUS;

/* Violet-only accent discipline (RLX-FEAT-008): neutral categories use the
   violet scale; red/amber stay reserved for risk semantics (bugs, tech debt). */
const ISSUE_TYPES: readonly { id: string; label: string; pct: number; color: string }[] = [
  { id: 'story', label: 'Stories', pct: 44, color: '#7c5cff' },
  { id: 'bug', label: 'Bugs', pct: 26, color: '#f87171' },
  { id: 'task', label: 'Tasks', pct: 19, color: '#c4b5fd' },
  { id: 'debt', label: 'Security', pct: 11, color: '#fbbf24' },
] as const;

const ESTIMATE_STATS: readonly { label: string; value: string; warn?: boolean }[] = [
  { label: 'Estimated (d)', value: '412d' },
  { label: 'Logged (d)', value: '465d' },
  { label: 'Deviation (d)', value: '+53d', warn: true },
  { label: 'Deviation (%)', value: '+12.9%', warn: true },
] as const;

function useInView<T extends HTMLElement>(): [React.RefObject<T>, boolean] {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setInView(true);
            obs.disconnect();
            break;
          }
        }
      },
      { threshold: 0.25 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return [ref, inView];
}

function IssueTypeDistributionCard(): React.ReactElement {
  const [ref, inView] = useInView<HTMLElement>();
  let cumulative = 0;
  return (
    <article
      ref={ref}
      className={`diagnostic-card diagnostic-card--donut${inView ? ' is-visible' : ''}`}
    >
      <header className="diagnostic-card__head">
        <span className="diagnostic-card__eyebrow">Issue type distribution</span>
        <span className="diagnostic-card__range">Last release</span>
      </header>
      <div className="diagnostic-card__body">
        <div className="diagnostic-card__donut" aria-hidden="true">
          <svg
            viewBox="0 0 120 120"
            focusable="false"
            role="presentation"
            style={{ ['--diagnostic-ring-full' as string]: `${RING_CIRCUM}` }}
          >
            <circle
              cx="60"
              cy="60"
              r={RING_RADIUS}
              stroke="rgba(148, 163, 184, 0.16)"
              strokeWidth="10"
              fill="none"
            />
            <g transform="rotate(-90 60 60)">
              {ISSUE_TYPES.map((seg, i) => {
                const len = (seg.pct / 100) * RING_CIRCUM;
                const offset = -cumulative;
                cumulative += len;
                return (
                  <circle
                    key={seg.id}
                    className="diagnostic-card__donut-seg"
                    cx="60"
                    cy="60"
                    r={RING_RADIUS}
                    stroke={seg.color}
                    strokeWidth="10"
                    fill="none"
                    strokeDashoffset={offset}
                    style={
                      {
                        ['--seg-target' as string]: `${len} ${RING_CIRCUM}`,
                        transitionDelay: inView ? `${i * 120}ms` : '0ms',
                      } as React.CSSProperties
                    }
                  />
                );
              })}
            </g>
          </svg>
          <div className="diagnostic-card__donut-label">
            <span className="diagnostic-card__donut-value">214</span>
            <span className="diagnostic-card__donut-caption">issues</span>
          </div>
        </div>
        <ul className="diagnostic-card__breakdown" aria-label="Issue type breakdown">
          {ISSUE_TYPES.map((seg) => (
            <li key={seg.id}>
              <span className={`diagnostic-card__bdot diagnostic-card__bdot--${seg.id}`} />
              <span>
                {seg.pct}% {seg.label}
              </span>
            </li>
          ))}
        </ul>
      </div>
      <footer className="diagnostic-card__foot">
        <p>Scope mix shapes how the release burns down.</p>
      </footer>
    </article>
  );
}

function QualityHotspotsCard(): React.ReactElement {
  const [ref, inView] = useInView<HTMLElement>();
  const max = Math.max(...HOTSPOTS.map((h) => h.count));
  return (
    <article
      ref={ref}
      className={`diagnostic-card diagnostic-card--hotspots${inView ? ' is-visible' : ''}`}
    >
      <header className="diagnostic-card__head">
        <span className="diagnostic-card__eyebrow">Quality hotspots</span>
        <span className="diagnostic-card__range">Bugs by area</span>
      </header>
      <ul className="diagnostic-card__bars" aria-label="Bug counts by module">
        {HOTSPOTS.map((row, i) => {
          const pct = Math.max(8, Math.round((row.count / max) * 100));
          return (
            <li
              key={row.module}
              className="diagnostic-card__bar-row"
              style={{ ['--bar-delay' as string]: `${i * 80}ms` }}
            >
              <span className="diagnostic-card__bar-label">{row.module}</span>
              <span className="diagnostic-card__bar-track">
                <span className="diagnostic-card__bar-fill" style={{ width: `${pct}%` }} />
              </span>
              <span className="diagnostic-card__bar-value">{row.count}</span>
            </li>
          );
        })}
      </ul>
      <footer className="diagnostic-card__foot">
        <p>Spot risky components before release decisions.</p>
      </footer>
    </article>
  );
}

function EstimateVsLoggedCard(): React.ReactElement {
  const [ref, inView] = useInView<HTMLElement>();
  return (
    <article
      ref={ref}
      className={`diagnostic-card diagnostic-card--stats${inView ? ' is-visible' : ''}`}
    >
      <header className="diagnostic-card__head">
        <span className="diagnostic-card__eyebrow">Estimate vs logged</span>
        <span className="diagnostic-card__range">Last release</span>
      </header>
      <div className="diagnostic-card__stats-grid" aria-label="Estimated versus logged effort">
        {ESTIMATE_STATS.map((stat, i) => (
          <div
            key={`${stat.label}-${i}`}
            className={`diagnostic-card__stat${stat.warn ? ' diagnostic-card__stat--warn' : ''}`}
          >
            <span className="diagnostic-card__stat-label">{stat.label}</span>
            <span className="diagnostic-card__stat-value">{stat.value}</span>
          </div>
        ))}
      </div>
      <footer className="diagnostic-card__foot">
        <p>See how far delivery drifted from the estimate.</p>
      </footer>
    </article>
  );
}

export function AnalyticsCards(): React.ReactElement {
  return (
    <div className="diagnostics-grid" role="list">
      <div role="listitem">
        <IssueTypeDistributionCard />
      </div>
      <div role="listitem">
        <QualityHotspotsCard />
      </div>
      <div role="listitem">
        <EstimateVsLoggedCard />
      </div>
    </div>
  );
}
